import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Star, MessageSquare, Loader2, RefreshCw, Filter,
  ThumbsUp, ThumbsDown, Bug, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const FeedbackPanel = () => {
  const [feedbackData, setFeedbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [revealEmails, setRevealEmails] = useState(false);

  useEffect(() => {
    loadFeedback();
  }, [selectedType, revealEmails]);

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const typeParam = selectedType === 'all' ? null : selectedType;
      const res = await api.getAdminFeedback(typeParam, 100, 0, revealEmails);
      setFeedbackData(res.data);
    } catch (err) {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getFeedbackTypeIcon = (type) => {
    switch (type) {
      case 'quick_feedback': return <ThumbsUp className="h-4 w-4" />;
      case 'bug': return <Bug className="h-4 w-4" />;
      case 'rating': return <Star className="h-4 w-4" />;
      case 'suggestion': return <MessageSquare className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getFeedbackTypeBadgeColor = (type) => {
    switch (type) {
      case 'quick_feedback': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
      case 'bug': return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      case 'rating': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
      case 'suggestion': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300';
      default: return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-emerald-600 dark:text-emerald-400 mb-2">
              <ThumbsUp className="h-6 w-6" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{feedbackData?.summary?.thumbs_up || 0}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Thumbs Up</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-red-500 dark:text-red-400 mb-2">
              <ThumbsDown className="h-6 w-6" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{feedbackData?.summary?.thumbs_down || 0}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Thumbs Down</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-amber-500 dark:text-amber-400 mb-2">
              <Star className="h-6 w-6" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {feedbackData?.summary?.by_type?.find(t => t.type === 'quick_feedback')?.avg_app_rating || 
               feedbackData?.summary?.by_type?.find(t => t.type === 'rating')?.avg_app_rating || 0}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Avg App Rating</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-sky-500 dark:text-sky-400 mb-2">
              <MessageSquare className="h-6 w-6" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{feedbackData?.total || 0}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Total Feedback</p>
          </CardContent>
        </Card>
      </div>

      {/* Feedback by Type Summary */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg dark:text-slate-100">Feedback by Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {feedbackData?.summary?.by_type?.map((item, i) => (
              <div key={i} className={`px-4 py-2 rounded-full ${getFeedbackTypeBadgeColor(item.type)}`}>
                <span className="font-medium capitalize">{item.type?.replace('_', ' ')}</span>
                <span className="ml-2 font-bold">{item.count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filter and Feedback List */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-lg dark:text-slate-100">All Feedback Entries</CardTitle>
              <CardDescription className="dark:text-slate-400">Detailed view of all user feedback (emails masked for PHI protection)</CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={revealEmails ? "default" : "outline"}
                size="sm"
                onClick={() => setRevealEmails(!revealEmails)}
                className="rounded-full text-xs"
                data-testid="reveal-emails-toggle"
              >
                {revealEmails ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                {revealEmails ? 'Hide Emails' : 'Reveal Emails'}
              </Button>
              <Filter className="h-4 w-4 text-slate-400" />
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-800 dark:text-slate-100"
                data-testid="feedback-type-filter"
              >
                <option value="all">All Types</option>
                <option value="quick_feedback">Quick Feedback & Ratings</option>
                <option value="rating">Legacy Ratings</option>
                <option value="suggestion">Suggestions</option>
                <option value="bug">Bug Reports</option>
              </select>
              <Button variant="ghost" size="sm" onClick={loadFeedback} className="rounded-full">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {feedbackData?.feedback?.length === 0 ? (
              <p className="text-center text-slate-400 py-8">No feedback entries found</p>
            ) : (
              feedbackData?.feedback?.map((item, i) => (
                <div key={i} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${getFeedbackTypeBadgeColor(item.feedback_type)}`}>
                        {getFeedbackTypeIcon(item.feedback_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {item.user_email || 'Anonymous'}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getFeedbackTypeBadgeColor(item.feedback_type)}`}>
                            {item.feedback_type?.replace('_', ' ')}
                          </span>
                        </div>
                        {item.user_specialty && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">{item.user_specialty}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">{formatDate(item.timestamp)}</span>
                  </div>
                  
                  {/* Content based on feedback type */}
                  <div className="mt-3 pl-12">
                    {item.feedback_type === 'quick_feedback' && item.content && (item.content === 'thumbs_up' || item.content === 'thumbs_down') && (
                      <div className="flex items-center gap-2">
                        {item.content === 'thumbs_up' ? (
                          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                            <ThumbsUp className="h-5 w-5" /> Thumbs Up
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-500 dark:text-red-400">
                            <ThumbsDown className="h-5 w-5" /> Thumbs Down
                          </span>
                        )}
                      </div>
                    )}
                    
                    {(item.app_rating || item.accuracy_rating) && (
                      <div className="flex flex-wrap gap-4 text-sm">
                        {item.app_rating && (
                          <span className="text-slate-600 dark:text-slate-300">
                            App Rating: <span className="font-medium text-amber-600 dark:text-amber-400">{item.app_rating}/5</span>
                          </span>
                        )}
                        {item.accuracy_rating && (
                          <span className="text-slate-600 dark:text-slate-300">
                            Accuracy: <span className="font-medium text-sky-600 dark:text-sky-400">{item.accuracy_rating}/5</span>
                          </span>
                        )}
                      </div>
                    )}
                    
                    {item.content && item.content !== 'thumbs_up' && item.content !== 'thumbs_down' && (
                      <p className="text-sm text-slate-700 dark:text-slate-300 mt-2 bg-white dark:bg-slate-700 p-3 rounded border border-slate-200 dark:border-slate-600">
                        {item.content}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackPanel;
